import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { GraphImg } from "@/components/GraphImg";
import { ageOf } from "@/components/OltStatus";
import { Button } from "@/components/ui/button";
import { graphTargets, portGraph, type GraphWindow } from "@/lib/api";
import { formatBps } from "@/lib/format";
import { fmtNum, useI18n } from "@/lib/i18n";
import type { OLT, OLTPortStat } from "@/lib/types";
import { cn } from "@/lib/utils";

const WINDOWS: GraphWindow[] = ["1h", "6h", "24h", "7d", "30d"];

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-rule bg-panel">
      <header className="border-b border-rule px-4 py-2.5">
        <h3 className="eyebrow">{title}</h3>
        {note && <p className="mt-1 text-[12px] text-slate-500">{note}</p>}
      </header>
      {children}
    </section>
  );
}

/**
 * Traffic from the OLT's point of view: TX is what the OLT sends (towards the
 * ONTs on a PON port, towards the core on an uplink), RX what it receives.
 * Two honest sources: the OLT's own CLI counters (current only) and the
 * LibreNMS interfaces (with speed, utilisation and history graphs).
 */
export default function OLTTraffic({ olt, cliPorts, nowMs }: { olt: OLT; cliPorts: OLTPortStat[]; nowMs: number }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState<number | null>(null);
  const [win, setWin] = useState<GraphWindow>("24h");
  const targets = useQuery({ queryKey: ["graph-targets", olt.mgmt_ip], queryFn: () => graphTargets(olt.mgmt_ip), staleTime: 60_000 });
  const key = olt.mgmt_ip.trim().toLowerCase();
  const device = targets.data?.devices.find((d) => d.hostname.trim().toLowerCase() === key || d.name.trim().toLowerCase() === key);

  return (
    <div className="space-y-4">
      <p className="rounded-md border border-rule bg-panel px-4 py-2.5 text-[12.5px] text-slate-400">
        <span className="eyebrow mr-2 text-slate-300">{t("olt.tr.perspective")}</span>
        <span className="data text-slate-200">↓ TX</span> {t("olt.tr.txMeans")} · <span className="data text-slate-200">↑ RX</span> {t("olt.tr.rxMeans")}
      </p>

      {cliPorts.length > 0 && (
        <Section title={t("olt.tr.cli")} note={t("olt.tr.cliNote")}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left">
                {[t("oltPorts.port"), "↓ TX", "↑ RX", t("oltPorts.errors"), t("olt.tr.sampled")].map((h, i) => (
                  <th key={h} className={cn("eyebrow px-4 py-2 font-medium", i > 0 && "text-right")}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cliPorts.map((p) => {
                const errs = (p.rx_errors ?? 0) + (p.tx_errors ?? 0);
                // No counters yet (first sample) is "—", never "0 bps".
                const rate = (v: number | null) => (v === null ? <span className="text-slate-600">—</span> : formatBps(v));
                return (
                  <tr key={p.id} className="border-b border-rule/60 last:border-b-0">
                    <td className="data px-4 py-2 text-slate-100">{p.name}</td>
                    <td className="data px-4 py-2 text-right text-slate-200">{rate(p.tx_bps)}</td>
                    <td className="data px-4 py-2 text-right text-slate-200">{rate(p.rx_bps)}</td>
                    <td className={cn("data px-4 py-2 text-right", errs > 0 ? "text-signal-warn" : "text-slate-500")}>
                      {p.rx_errors === null && p.tx_errors === null ? "—" : fmtNum(locale, errs)}
                    </td>
                    <td className="data px-4 py-2 text-right text-slate-500">{p.sampled_at ? t("olt.ago", { age: ageOf(p.sampled_at, nowMs) }) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      <Section title={t("olt.tr.history")} note={device ? t("olt.tr.historyNote") : undefined}>
        {targets.isLoading ? (
          <p className="px-4 py-3 text-[12.5px] text-slate-500">{t("common.loading")}</p>
        ) : !targets.data?.librenms_configured ? (
          <p className="px-4 py-3 text-[12.5px] text-slate-500">{t("common.librenmsNotConfigured")}</p>
        ) : !device || device.ports.length === 0 ? (
          <p className="px-4 py-3 text-[12.5px] text-slate-500">{t("olt.tr.notMirrored")}</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left">
                {[t("oltPorts.port"), t("common.status"), "↓ TX", "↑ RX", t("olt.tr.speed"), t("olt.tr.util")].map((h, i) => (
                  <th key={h} className={cn("eyebrow px-4 py-2 font-medium", i > 1 && "text-right")}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {device.ports.map((p) => {
                const up = p.oper_status === "up";
                const util = p.speed_bps && (p.rx_bps !== null || p.tx_bps !== null)
                  ? (Math.max(p.rx_bps ?? 0, p.tx_bps ?? 0) / p.speed_bps) * 100 : null;
                const isOpen = open === p.port_id;
                return (
                  <Fragment key={p.port_id}>
                    <tr onClick={() => setOpen(isOpen ? null : p.port_id)}
                      className={cn("cursor-pointer border-b border-rule/60 hover:bg-white/[0.02]", isOpen && "bg-white/[0.03]")}>
                      <td className="px-4 py-2">
                        <span className="data text-slate-100">{p.ifname}</span>
                        {p.ifalias && <span className="ml-2 text-[11.5px] text-slate-500">{p.ifalias}</span>}
                      </td>
                      <td className="px-4 py-2">
                        {p.oper_status ? (
                          <span className={cn("inline-flex items-center gap-1.5 text-[12px]", up ? "text-signal-good" : "text-slate-500")}>
                            <span className={cn("size-1.5 rounded-full", up ? "bg-signal-good" : "border border-slate-500")} />
                            {up ? t("olt.reach.up") : t("olt.reach.down")}
                          </span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="data px-4 py-2 text-right text-slate-200">{up ? formatBps(p.tx_bps) : "—"}</td>
                      <td className="data px-4 py-2 text-right text-slate-200">{up ? formatBps(p.rx_bps) : "—"}</td>
                      <td className="data px-4 py-2 text-right text-slate-400">{p.speed_bps ? formatBps(p.speed_bps) : "—"}</td>
                      <td className={cn("data px-4 py-2 text-right",
                        util === null ? "text-slate-600" : util > 80 ? "text-signal-crit" : util >= 50 ? "text-signal-warn" : "text-slate-300")}>
                        {util === null || !up ? "—" : util < 0.1 ? "<0.1%" : `${util.toFixed(1)}%`}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-rule/60">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="mb-2 flex items-center gap-1">
                            {WINDOWS.map((w) => (
                              <Button key={w} size="xs" variant={w === win ? "default" : "secondary"} onClick={() => setWin(w)}>{w}</Button>
                            ))}
                          </div>
                          <GraphImg
                            queryKey={["graph", device.device_id, p.port_id, win]}
                            fetch={(w) => portGraph(device.device_id, p.port_id, win, w)}
                            alt={t("graphs.alt", { device: device.name, port: p.ifname, win })}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
